import Phaser from 'phaser';
import * as C from 'tower/constants';
import {
  State,
  StateMachine,
  Direction,
  directionalVelocity,
  gridMove,
  oppositeDirection,
  DIRECTIONS,
  wait,
  frameMs,
  asyncTween,
  relativeMove,
  relativeDirection,
  setVelocityTowards,
  wouldHit,
  withinFacingCone,
} from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import Enemy from 'tower/scenes/battle/enemy';
import gameState from 'tower/state/game-state';
import FlashableSprite from 'tower/flashable';
import Boomerang from './boomerang';
import Flame from './flame';
import Projectile from './enemy/projectile';
import { HitOptions } from 'tower/types';
import Interactable from './interactable';

export default class Player extends FlashableSprite {
  stateMachine: StateMachine<PlayerState>;
  declare body: Phaser.Physics.Arcade.Body;
  invulnerable = false;
  canBeDropped = true;

  /** Track which items have active projectiles and cannot be thrown a second time until destroyed/returned. */
  itemThrown = {
    boomerang: false,
    arrow: false,
  };

  private _direction: Direction = Direction.Left;

  constructor(
    public scene: BattleScene,
    x: number,
    y: number,
  ) {
    super(scene, x, y, 'link', 0);
    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.setDepth(C.DEPTHS.Player);
    this.body.setSize(8, 8).setOffset(4, 7);
    this.body.pushable = false;

    this.direction = Direction.Down;

    this.stateMachine = new StateMachine('idle', {
      idle: new IdleState(scene, this),
      moving: new MovingState(scene, this),
      sword: new SwordState(scene, this),
      interact: new InteractState(scene, this),
      'subweapon[mattock]': new MattockState(scene, this, 11),
      'subweapon[betterMattock]': new MattockState(scene, this, 13),
      'subweapon[bestMattock]': new MattockState(scene, this, 15),
      'subweapon[boomerang]': new BoomerangState(scene, this),
      'subweapon[candle]': new CandleState(scene, this),
      'subweapon[bow]': new BowState(scene, this),
      'subweapon[potion]': new PotionState(scene, this),
      caught: new CaughtState(scene, this),
      holdingItem: new HoldingItemState(scene, this),
      dragging: new DraggingState(scene, this),
      dropped: new DroppedState(scene, this),
      hurt: new HurtState(scene, this),
      death: new DeathState(scene, this),
    });

    this.on('dragstart', () => {
      this.stateMachine.transition('dragging');
    });
  }

  get palette() {
    return this._palette + (gameState.hasItem('blueTunic') ? 4 : 0) + (gameState.hasItem('betterShield') ? 8 : 0);
  }

  set palette(value: number) {
    super.palette = value;
  }

  get direction() {
    return this._direction;
  }

  set direction(newDirection: Direction) {
    this._direction = newDirection;
    if (newDirection === Direction.Left) {
      this.setFlipX(true);
    } else {
      this.setFlipX(false);
    }
  }

  hit(options: HitOptions, skipInvlun = false) {
    if (this.invulnerable && !skipInvlun) {
      return;
    }

    let blockedProjectiles = ['boomerang'];
    if (gameState.hasItem('betterShield')) {
      blockedProjectiles = ['boomerang', 'magic', 'wallMagic', 'fireball', 'arrow'];
    }

    // Blocks projectiles from the front
    if (
      blockedProjectiles.includes(options.weaponType) &&
      withinFacingCone(this, options.weaponBody.prevFrame, this.direction, 135)
    ) {
      this.scene.sound.play('deflectSmall');
      if (options.weaponType === 'boomerang') {
        this.scene.events.emit('boomerangblocked');
      }
      return;
    }

    let damage = options.damage;
    if (gameState.hasItem('blueTunic')) {
      damage = Math.ceil(damage / 2);
    }
    gameState.hp -= damage;
    if (gameState.hp <= 0) {
      this.stateMachine.transition('death', options);
    } else {
      this.stateMachine.transition('hurt', options);
    }
  }

  enableDrag() {
    this.setInteractive({
      useHandCursor: true,
      draggable: true,
    });
  }

  disableDrag() {
    this.disableInteractive();
  }

  catch() {
    this.stateMachine.transition('caught');
  }

  holdItem(item: Phaser.GameObjects.Image) {
    this.stateMachine.transition('holdingItem');
    item.setPosition(this.x, this.y - 16);
  }

  idle() {
    this.stateMachine.transition('idle');
  }

  showSwing() {
    switch (this.direction) {
      case Direction.Up:
        this.setFrame(8);
        break;
      case Direction.Down:
        this.setFrame(6);
        break;
      case Direction.Right:
        this.setFrame(7);
        break;
      case Direction.Left:
        this.setFrame(7);
        break;
    }
  }

  findInteractable() {
    for (const interactableObject of this.scene.currentRoom.interactables.children) {
      const interactable = interactableObject as Interactable;
      if (wouldHit(this.scene, this, interactable, ...gridMove(0, 0, this.direction, 4))) {
        return interactable;
      }
    }

    return null;
  }

  update() {
    this.stateMachine.update();
  }

  static preload(scene: Phaser.Scene) {
    Boomerang.preload(scene);

    scene.load.spritesheet('link', 'img/link.png', { frameWidth: 16, frameHeight: 16 });
    scene.load.spritesheet('sword', 'img/sword.png', { frameWidth: 16, frameHeight: 16 });
    scene.load.audio('sword', 'audio/sword.mp3');
    scene.load.audio('playerHurt', 'audio/player_hurt.mp3');
    scene.load.audio('bomb', 'audio/bomb.mp3');
  }

  static create(scene: BattleScene) {
    Boomerang.create(scene);

    scene.anims.create({
      key: `playerWalk[down]`,
      frameRate: 5,
      frames: scene.anims.generateFrameNumbers('link', {
        frames: [1, 0],
      }),
      repeat: -1,
    });
    scene.anims.create({
      key: `playerWalk[right]`,
      frameRate: 5,
      frames: scene.anims.generateFrameNumbers('link', {
        frames: [3, 2],
      }),
      repeat: -1,
    });
    scene.anims.create({
      key: `playerWalk[left]`,
      frameRate: 5,
      frames: scene.anims.generateFrameNumbers('link', {
        frames: [3, 2],
      }),
      repeat: -1,
    });
    scene.anims.create({
      key: `playerWalk[up]`,
      frameRate: 5,
      frames: scene.anims.generateFrameNumbers('link', {
        frames: [5, 4],
      }),
      repeat: -1,
    });
    scene.anims.create({
      key: `playerSpin`,
      frameRate: 5,
      frames: scene.anims.generateFrameNumbers('link', {
        frames: [0, 2, 4, 10, 0, 2, 4, 10, 0, 2, 4, 10, 0, 2, 4, 10, 0],
      }),
      repeat: 0,
    });
    scene.anims.create({
      key: `playerDie`,
      frameRate: 5,
      frames: scene.anims.generateFrameNumbers('link', {
        frames: [11, 11, 12],
      }),
      repeat: 0,
    });
  }
}

class PlayerState extends State {
  constructor(
    protected scene: BattleScene,
    protected player: Player,
  ) {
    super();
  }
}

class IdleState extends PlayerState {
  handleEntered() {
    this.player.stop();
    this.player.body.setVelocity(0);

    let frame = 4;
    switch (this.player.direction) {
      case Direction.Left:
      case Direction.Right:
        frame = 2;
        break;
      case Direction.Down:
        frame = 0;
        break;
    }
    this.player.setFrame(frame);

    this.player.emit('idle');
  }

  update() {
    const { scene, player } = this;
    const { controller } = scene;

    if (controller.button1.justDown) {
      const interactable = player.findInteractable();
      if (interactable !== null) {
        return this.transition('interact', interactable);
      }

      return this.transition('sword');
    }
    if (controller.button2.justDown && gameState.subweapon !== null && !gameState.subweaponsDisabled) {
      return this.transition(`subweapon[${gameState.subweapon}]`);
    }

    for (const direction of DIRECTIONS) {
      if (controller[direction].isDown) {
        return this.transition('moving', direction);
      }
    }
  }
}

class MovingState extends PlayerState {
  direction!: Direction;

  handleEntered(direction?: Direction) {
    const { player } = this;

    // Edge case when returning from hurt
    if (!direction) {
      return this.transition('idle');
    }

    this.direction = direction;
    player.direction = direction;
    player.play(`playerWalk[${direction}]`);
    player.body.setVelocity(...directionalVelocity(C.PLAYER_VELOCITY, direction));

    player.emit('moving', direction);
  }

  update() {
    const { player } = this;
    const { controller } = this.scene;

    if (controller.button1.justDown) {
      const interactable = player.findInteractable();
      if (interactable !== null) {
        return this.transition('interact', interactable);
      }

      return this.transition('sword');
    }
    if (controller.button2.justDown && gameState.subweapon !== null && !gameState.subweaponsDisabled) {
      return this.transition(`subweapon[${gameState.subweapon}]`);
    }

    for (const direction of DIRECTIONS) {
      if (direction != this.player.direction && controller[direction].justDown) {
        return this.transition('moving', direction);
      }
    }

    if (!this.scene.controller[this.player.direction].isDown) {
      return this.transition('idle');
    }

    player.body.setVelocity(...directionalVelocity(C.PLAYER_VELOCITY, this.direction));
  }
}

class SwordState extends PlayerState {
  sword!: Phaser.Physics.Arcade.Sprite;

  async handleEntered() {
    const swordKey = gameState.sword;
    if (swordKey === null) {
      return this.transition('idle');
    }

    const { scene, player } = this;
    player.stop();
    player.body.setVelocity(0);

    player.emit('sword');

    const swordVariant = C.SWORD_VARIANTS[swordKey];
    const sword = scene.physics.add
      .sprite(player.x, player.y, 'sword', swordVariant.frame)
      .setDepth(C.DEPTHS.Sword)
      .setVisible(false);
    this.sword = sword;
    relativeMove(sword, player.direction, 14);

    const hitEnemies = new Set();
    const collider = scene.physics.add.overlap(sword, scene.currentRoom.enemies, (_sword, enemySprite) => {
      const enemy = enemySprite as Enemy;
      if (!hitEnemies.has(enemy)) {
        enemy.hit({
          weaponType: 'sword',
          weaponBody: sword.body,
          damage: swordVariant.damage,
          directionOverride: player.direction,
        });
        hitEnemies.add(enemy);
      }
    });
    collider.name = 'Sword to enemies';

    player.showSwing();
    switch (player.direction) {
      case Direction.Up:
        sword.body.setSize(6, 16);
        break;
      case Direction.Down:
        sword.setRotation(Phaser.Math.DegToRad(180));
        sword.body.setSize(6, 16).setOffset(6, 0);
        break;
      case Direction.Right:
        sword.setFrame(swordVariant.frame + 1);
        sword.body.setSize(16, 6).setOffset(1, 6);
        break;
      case Direction.Left:
        sword.setFrame(swordVariant.frame + 1).setFlipX(true);
        sword.body.setSize(16, 6).setOffset(0, 6);
        break;
    }

    await wait(scene, frameMs(3));
    sword.setVisible(true);
    scene.sound.play('sword');

    await wait(scene, frameMs(6));

    const [dx, dy] = gridMove(0, 0, oppositeDirection(player.direction), 10);
    await asyncTween(scene, {
      targets: sword,
      x: `+=${dx}`,
      y: `+=${dy}`,
      duration: frameMs(3),
      ease: Phaser.Math.Easing.Stepped,
    });

    sword.destroy();
    collider.destroy();

    return this.transition('idle');
  }

  handleExited() {
    this.sword?.destroy();
  }
}

class InteractState extends PlayerState {
  async handleEntered(interactable: Interactable) {
    const { scene, player } = this;
    player.stop();
    player.body.setVelocity(0);

    player.showSwing();
    interactable.interact();
    await wait(scene, frameMs(6));

    return this.transition('idle');
  }
}

class MattockState extends PlayerState {
  constructor(
    scene: BattleScene,
    player: Player,
    private frame: number,
  ) {
    super(scene, player);
  }

  async handleEntered() {
    const { scene, player } = this;
    player.stop();
    player.body.setVelocity(0);
    player.showSwing();

    if (gameState.mattockDurability > 0) {
      const mattock = scene.physics.add
        .sprite(player.x, player.y, 'items', this.frame)
        .setDepth(C.DEPTHS.Items)
        .setVisible(false);
      switch (player.direction) {
        case Direction.Up:
          mattock.setRotation(-Math.PI / 2).setPosition(player.x - 4, player.y - 14);
          break;
        case Direction.Down:
          mattock.setRotation(Math.PI / 2).setPosition(player.x + 4, player.y + 14);
          break;
        case Direction.Right:
          mattock.setPosition(player.x + 14, player.y);
          break;
        case Direction.Left:
          mattock.setFlipX(true).setPosition(player.x - 14, player.y);
          break;
      }

      await wait(scene, frameMs(3));
      mattock.setVisible(true);
      scene.sound.play('sword');

      await wait(scene, frameMs(3));
      mattock.setFrame(this.frame + 8);

      // If the room has the 'mattockImmune' tag, the mattock doesn't work in that room.
      if (!scene.currentRoom.roomConfig.flags?.includes('mattockImmune')) {
        const { maze } = scene.currentRoom;
        const nodes = maze.getNodesAtWorldXY(player.x, player.y);
        const walls = maze.getWallsAtWorldXY(player.x, player.y);
        if (walls[player.direction]) {
          let destroyed: boolean;
          switch (player.direction) {
            case Direction.Up:
              destroyed = maze.removeWall(nodes[0], Direction.Right);
              break;
            case Direction.Right:
              destroyed = maze.removeWall(nodes[1], Direction.Down);
              break;
            case Direction.Down:
              destroyed = maze.removeWall(nodes[2], Direction.Right);
              break;
            case Direction.Left:
              destroyed = maze.removeWall(nodes[0], Direction.Down);
              break;
          }

          if (destroyed) {
            scene.sound.play('bomb');
            gameState.mattockDurability--;
          }
        }
      }

      await wait(scene, frameMs(6));
      mattock.destroy();
    } else {
      await wait(scene, frameMs(12));
    }

    return this.transition('idle');
  }
}

class BoomerangState extends PlayerState {
  async handleEntered() {
    const { scene, player } = this;
    if (player.itemThrown.boomerang) {
      return this.transition('idle');
    }

    player.itemThrown.boomerang = true;
    player.stop();
    player.body.setVelocity(0);

    player.showSwing();

    const boomerang = new Boomerang(
      scene,
      ...gridMove(player.x, player.y, player.direction, 16),
      player.direction,
      'enemy',
      player,
    );
    boomerang.on('destroy', () => (player.itemThrown.boomerang = false));

    await wait(scene, frameMs(6));

    return this.transition('idle');
  }
}

class CaughtState extends PlayerState {
  async handleEntered() {
    const { player } = this;

    player.stop().showSwing();
    player.body.setVelocity(0);
    await wait(this.scene, frameMs(6));

    return this.transition('idle');
  }
}

class CandleState extends PlayerState {
  started = 0;
  delay = 0;
  collectBody: Phaser.Physics.Arcade.Body | null = null;

  handleEntered() {
    const { scene, player } = this;

    this.started = scene.time.now;

    player.stop();
    player.body.setVelocity(0);
    player.showSwing();

    if (gameState.candleDurability > 0) {
      const flame = new Flame(scene, scene.currentRoom, ...gridMove(player.x, player.y, player.direction, 16), {
        direction: player.direction,
        limitHits: true,
      });
      scene.currentRoom.misc.add(flame);
      scene.sound.play('flame');
      gameState.candleDurability--;

      // Cannot collect flame if just used
      this.collectBody = null;
      this.delay = frameMs(8);
    } else {
      // Make collect body + longer delay for easier collection
      this.collectBody = scene.physics.add.body(player.x - 8, player.y - 8, 16, 16);
      this.delay = frameMs(15);
    }
  }

  update() {
    const { scene } = this;

    // If we're collecting and a fireball hits us, relight the candle and destory the fireball.
    if (
      this.collectBody !== null &&
      scene.physics.overlap(
        scene.currentRoom.projectiles,
        this.collectBody,
        (projectileObject) => {
          projectileObject.destroy(true);
        },
        (projectileObject, _) => {
          return (projectileObject as Projectile).variantKey === 'fireball';
        },
      )
    ) {
      gameState.candleDurability = gameState.maxCandleDurability;
      scene.sound.play('fairy');
      return this.transition('idle');
    }

    if (scene.time.now - this.started > this.delay) {
      return this.transition('idle');
    }
  }

  handleExited() {
    if (this.collectBody !== null) {
      this.collectBody.destroy();
    }
  }
}

class BowState extends PlayerState {
  async handleEntered() {
    const { scene, player } = this;

    if (player.itemThrown.arrow) {
      return this.transition('idle');
    }

    player.itemThrown.arrow = true;
    player.stop();
    player.body.setVelocity(0);
    player.showSwing();

    const arrow = new Projectile(
      scene,
      ...gridMove(player.x, player.y, player.direction, 4),
      'arrow',
      player.direction,
      { target: 'enemy' },
    );
    scene.sound.play('blip');
    arrow.on('destroy', () => (player.itemThrown.arrow = false));

    await wait(scene, frameMs(10));

    return this.transition('idle');
  }
}

class PotionState extends PlayerState {
  async handleEntered() {
    const { scene, player } = this;

    player.stop();
    player.body.setVelocity(0);
    scene.freeze();

    gameState.removeItem('potion');
    await scene.healPlayer(100);

    scene.unfreeze();

    return this.transition('idle');
  }
}

class HoldingItemState extends PlayerState {
  handleEntered() {
    this.player.stop().setFrame(13);
    this.player.body.setVelocity(0);
    // The item class controls the player for this and will transition them to idle once the item is collected
  }
}

class DraggingState extends PlayerState {
  prevPointerX = -1;

  handleEntered() {
    const { scene, player } = this;
    player.body.stop();
    player.stop().setFrame(13).setOrigin(0.1, 0.1).setFlipX(false);
    scene.cameras.main.stopFollow();
    scene.sound.play('squeak');

    this.prevPointerX = scene.input.activePointer.x;
  }

  update() {
    const { scene, player } = this;

    // Checks for if dragging has ended as a fallback if the dragend event happened while the player was held over
    // lava and could not be dropped.
    if (player.input?.dragState === 0 && player.canBeDropped) {
      return this.drop();
    }

    // Drop the player if they hit a wall.
    if (!player.body.blocked.none && player.canBeDropped) {
      return this.drop();
    }

    // Drop the player if they exit the room bounds
    if (
      !Phaser.Geom.Rectangle.ContainsRect(
        scene.currentRoom.rect,
        new Phaser.Geom.Rectangle(player.body.x, player.body.y, player.body.width, player.body.height),
      )
    ) {
      return this.drop();
    }

    const { activePointer } = scene.input;
    activePointer.updateWorldPoint(scene.cameras.main);

    // Add to player swinging if moving
    const diffX = activePointer.x - this.prevPointerX;
    const magnitude = Math.min(Math.abs(diffX) * 2, 10);
    const diffSign = diffX < 0 ? -1 : 1;
    player.angle += magnitude * diffSign;

    // If we aren't swinging, trend towards 15
    const target = 20;
    if (magnitude < 1) {
      if (player.angle > target) {
        player.angle = Math.max(player.angle - 2, target);
      } else if (player.angle < target) {
        player.angle = Math.min(player.angle + 2, target);
      }
    }

    // Cap player swing at 45 degrees
    if (player.angle < -45) {
      player.angle = -45;
    } else if (player.angle > 45) {
      player.angle = 45;
    }

    this.prevPointerX = scene.input.activePointer.x;

    // Move player to mouse
    player.setPosition(activePointer.worldX, activePointer.worldY);

    // Apply a tiny velocity so that collision checks still occur
    scene.physics.moveTo(player, activePointer.worldX, activePointer.worldY, 0.01);
  }

  drop() {
    const { scene, player } = this;

    player.setOrigin(0.5, 0.5).setAngle(0);

    // Push the player back into the room if needed.
    const { rect } = scene.currentRoom;
    if (player.body.left < rect.left) {
      player.body.x = rect.left + 8;
    }
    if (player.body.right > rect.right) {
      player.body.x = rect.right - 8;
    }
    if (player.body.top < rect.top) {
      player.body.y = rect.top;
    }
    if (player.body.bottom > rect.bottom) {
      player.body.y = rect.bottom - 8;
    }
    player.setPosition(player.body.x + 2, player.body.y + 2);

    return this.transition('dropped');
  }
}

class DroppedState extends PlayerState {
  async handleEntered() {
    const { scene, player } = this;

    player.disableDrag();
    player.setFrame(14);
    scene.sound.play('bomb');
    scene.cameras.main.pan(player.x, player.y, 300);

    await wait(scene, 350);

    scene.cameras.main.startFollow(player);
    player.enableDrag();

    return this.transition('idle');
  }
}

class HurtState extends PlayerState {
  async handleEntered({ weaponBody, directionOverride }: HitOptions) {
    const { player, scene } = this;
    player.invulnerable = true;
    player.flash(6).on('complete', () => {
      player.invulnerable = false;
    });
    scene.sound.play('playerHurt');

    const pushTowards = directionOverride ?? oppositeDirection(relativeDirection(player.body, weaponBody));
    setVelocityTowards(player.body, pushTowards, C.PLAYER_PUSH_SPEED);
    await wait(scene, frameMs(C.PLAYER_PUSH_FRAMES));

    player.body.setVelocity(0);
    return this.stateMachine.transition('idle');
  }
}

class DeathState extends PlayerState {
  handleEntered() {
    const { scene, player } = this;
    player.body.setVelocity(0);
    scene.sound.play('playerHurt');
    scene.handlePlayerDeath();
  }
}
